import React, { ReactElement } from 'react';

import { Inject } from '@odinjs/odin';

import { Endpoint, ToastService } from '@philips-emr/hpms-provider-commons';
import { ActionAPI, AutocompleteAPI, ContextualBarAPI, FetchAPI, Field, FieldAPI, InputAPI, Link, Panel, PanelAPI, Text, Token } from '@philips-emr/tws-components';
import { LayoutFlexDirection, Size, ToastLevel, Type } from '@philips-emr/tws-constants';
import Controller, { OnAfterFetch, OnCancel, OnChange, OnClick, OnClose, OnDynamicContentRender, OnMount, OnSubmit } from '@philips-emr/tws-core/lib/client';
import { ChangeEvent, FetchEvent, MountEvent, RenderEvent, SubmitEvent } from '@philips-emr/tws-core/lib/component/types';
import { HttpService } from '@philips-emr/tws-core/lib/services';
import { generateUUID, isEmptyArrayOrObject } from '@philips-emr/tws-utils';

import { ReactComponent as MagnifierIcon } from '../../../resources/assets/search.svg';
import { NegotiationContractualAmendmentAliases as Aliases, FragmentNames } from '../../codegen';
import BaseController from '../BaseController';

import { AmendmentType, chooseMargin, ContractualDraft } from './negotiation-contractual-draft-controller';

@Controller({ fragment: FragmentNames.NEGOTIATION_CONTRACTUAL_AMENDMENT })
class NegotiationContractualAmendmentController extends BaseController {

  @Inject(Aliases.PANEL_CLAUSE)
  private panelClause: PanelAPI;
  @Inject(Aliases.PANEL_PROPOSAL)
  private panelProposal: PanelAPI;
  @Inject(Aliases.PANEL_LOADER)
  private panelLoader: PanelAPI;
  @Inject(Aliases.PANEL_ATTACHMENTS)
  private panelAttachments: PanelAPI;
  @Inject(Aliases.FETCH_CLAUSE)
  private fetchClause: FetchAPI;
  @Inject(Aliases.CONTEXTUAL_BAR_CLAUSE)
  private contextualBarClause: ContextualBarAPI;
  @Inject(Aliases.FETCH_CLAUSES)
  private fetchClauses: FetchAPI;
  @Inject(Aliases.ACTION_REQUEST_CHANGE)
  private actionRequestChange: ActionAPI;
  @Inject(Aliases.PANEL_REQUEST_CHANGE)
  private panelRequestChange: PanelAPI;
  @Inject(Aliases.FIELD_COUNTER_OFFER)
  private fieldCounterOffer: FieldAPI;
  @Inject(Aliases.AUTOCOMPLETE_MENU_MOBILE)
  private autocompleteMenuMobile: AutocompleteAPI;

  @OnMount(Aliases.FETCH_CLAUSES)
  onMountFetchClauses(event: MountEvent): void {
    const api = event.target as FetchAPI;
    const { nrSeqNegotiation, nrSeqProposal } = this.historyService.getCurrentRouteParams();
    api.setParameters({ nrSeqNegotiation, nrSeqProposal });
    api.forceFetch();
  }

  @OnMount(Aliases.FETCH_MENU_MOBILE)
  onMountFetchMenuMobile(event: MountEvent): void {
    const api = event.target as FetchAPI;
    const { nrSeqNegotiation, nrSeqProposal } = this.historyService.getCurrentRouteParams();
    api.setParameters({ nrSeqNegotiation, nrSeqProposal, mobile: true });
    api.forceFetch();
  }

  @OnDynamicContentRender(Aliases.FIELD_CLAUSES)
  onRenderClauses(event: RenderEvent): void {
    const { data, render } = event;
    if (!isEmptyArrayOrObject(data)) {
      const { draftTitle, nrSeqItemProposal, secondLevel, thirdLevel, fourthLevel } = data as ContractualDraft;
      const margin = chooseMargin(secondLevel, thirdLevel, fourthLevel);

      render((): ReactElement =>
        <Panel className="" uuid={generateUUID()} margin={margin}>
          <Link value={draftTitle} onClick={() => this.forceFetchClause(nrSeqItemProposal, draftTitle)} size={Size.MEDIUM} />
        </Panel>);
    }
  }

  @OnDynamicContentRender(Aliases.FIELD_BULLET)
  onRenderBullet(event: RenderEvent): void {
    const { data, render } = event;
    const { secondLevel, thirdLevel, fourthLevel, ieAdditionType } = data as ContractualDraft;
    const margin = chooseMargin(secondLevel, thirdLevel, fourthLevel);
    if (!isEmptyArrayOrObject(data) && ieAdditionType != AmendmentType.ORIGINAL) {
      render((): ReactElement =>
        <Panel className="" uuid={generateUUID()} margin={margin}>
          <div className="u-token-bullet">
            <Token type={this.getTokenType(ieAdditionType)} value={''} />
          </div>
        </Panel>);
    }
  }

  @OnDynamicContentRender(Aliases.TOKEN_AMENDMENT_TYPE)
  onRenderAmendmentType(event: RenderEvent): void {
    const { data, render } = event;
    const { ieAdditionType, additionType } = data as ContractualDraft;
    if (additionType) {
      render((): ReactElement =>
        <Field entry="" uuid={generateUUID()} width={2}>
          <Token type={this.getTokenType(ieAdditionType)} value={additionType} />
        </Field>
      );
    }
  }

  @OnDynamicContentRender(Aliases.TOKEN_STATUS)
  onRenderIncluded(event: RenderEvent): void {
    const { render, data } = event;
    const { vlDominio, dsValorDominio } = data as ContractualDraft;
    if (vlDominio != AmendmentType.ORIGINAL) {
      render((): ReactElement =>
        <Panel className=""
          uuid={generateUUID()}
          flexDirection={LayoutFlexDirection.ROW}
          margin={'0.3rem 0 0 0'}>
          <Field entry="" uuid={generateUUID()} width={1}>
            <div className="u-token-bullet-large">
              <Token type={this.getTokenType(vlDominio)} value={''} />
            </div>
          </Field>
          <Panel className="" uuid={generateUUID()} margin={'0.3rem 0 0 0.7rem'}>
            <Field entry="" uuid={generateUUID()} width={12}>
              <Text value={dsValorDominio} />
            </Field>
          </Panel>
        </Panel>);
    }
  }

  @OnClick(Aliases.LINK_PROPOSAL)
  onClickProposal(): void {
    this.panelProposal.setVisible(true);
    this.panelClause.setVisible(false);
    this.panelAttachments.setVisible(false);
  }

  @OnClick(Aliases.LINK_ATTACHMENT)
  onClickAttachment(): void {
    this.panelProposal.setVisible(false);
    this.panelClause.setVisible(false);
    this.panelAttachments.setVisible(true);
  }

  @OnMount(Aliases.INPUT_FILTER)
  onMountFilter(event: MountEvent): void {
    const api = event.target as InputAPI;
    api.setExternalAction(<MagnifierIcon className="c-module-selector__search-icon" />);
  }

  @OnChange(Aliases.INPUT_FILTER)
  onEditFilter(event: ChangeEvent<ContractualDraft>): void {
    const params = this.fetchClauses.getParameters();
    const draftTitle = event.value;
    this.fetchClauses.setParameters({ ...params, draftTitle });
    this.fetchClauses.forceFetch();
  }

  @OnAfterFetch(Aliases.FETCH_CLAUSE)
  onAfterFetch(event: FetchEvent): void {
    const data = event.data as ContractualDraft;
    if (data) {
      const { edit, ieValueCounterProposal } = data;
      this.actionRequestChange.setEnabled(edit == 'S');
      this.fieldCounterOffer.setVisible(ieValueCounterProposal == 'S');

    }
  }

  @OnChange(Aliases.AUTOCOMPLETE_MENU_MOBILE)
  onChange(event: ChangeEvent<ContractualDraft>): void {
    const { nrSeqItemProposal, draftTitle, proposalMenu, attachmentsMenu } = event.value;
    if (proposalMenu) {
      this.panelProposal.setVisible(true);
      this.panelClause.setVisible(false);
      this.panelAttachments.setVisible(false);
      return;
    }
    if (attachmentsMenu) {
      this.panelProposal.setVisible(false);
      this.panelClause.setVisible(false);
      this.panelAttachments.setVisible(true);
      return;
    }
    this.forceFetchClause(nrSeqItemProposal, draftTitle);
  }

  @OnClick(Aliases.ACTION_REQUEST_CHANGE)
  onClickAccept(): void {
    this.panelRequestChange.setVisible(true);
  }

  @OnSubmit(Aliases.FORM_REQUEST_CHANGE)
  onAcceptProposal(event: SubmitEvent): void {
    const { counterOffer } = event.record as ContractualDraft;
    const { nrSeqItemProposal, draftTitle } = this.context.getBulkData();
    const selectedItens = [nrSeqItemProposal];

    const body = { counterOffer, selectedItens };
    const { nrSeqProposal, nrSeqNegotiation } = this.historyService.getCurrentRouteParams();
    HttpService.post(Endpoint.POST.EDIT_NEGOTIATION_CONTRACT_DRAFT, { nrSeqProposal, nrSeqNegotiation, counterOffer }, { body }).then(() => {
      ToastService.open({ message: 'provider.request-sucessfull', level: ToastLevel.SUCCESS });
      this.forceFetchClause(nrSeqItemProposal as number, draftTitle as string);
      this.panelRequestChange.setVisible(false);
    }).catch((error) => {
      this.panelRequestChange.setVisible(false);
      ToastService.open(error.body);
    });
  }

  @OnClose(Aliases.MODAL_REQUEST_CHANGE)
  onCloseModalAccept(): void {
    this.panelRequestChange.setVisible(false);
  }

  @OnCancel(Aliases.FORM_REQUEST_CHANGE)
  onCancelFormAccept(): void {
    this.panelRequestChange.setVisible(false);
  }

  @OnAfterFetch(Aliases.FETCH_MENU_MOBILE)
  onAfterFetchMobile(event: FetchEvent): void {
    if (event.data && !isEmptyArrayOrObject(event.data)) {
      const data = event.data as [{}];
      this.autocompleteMenuMobile.setValue(data[0]);
    }
  }

  private forceFetchClause(nrSeqItemProposal: number, draftTitle: string): void {
    const visible = this.panelClause.isVisible();
    this.panelLoader.setVisible(!visible);
    this.panelClause.setVisible(true);
    this.panelProposal.setVisible(false);
    this.panelAttachments.setVisible(false);
    this.context.set({ nrSeqItemProposal, draftTitle });
    setTimeout(() => {
      const { nrSeqNegotiation, nrSeqProposal } = this.historyService.getCurrentRouteParams();
      this.fetchClause.setParameters({ nrSeqNegotiation, nrSeqProposal, nrSeqItemProposal });
      this.fetchClause.forceFetch();
      this.contextualBarClause.setTitle(draftTitle);
      this.panelLoader.setVisible(false);
    }, visible ? 0 : 500);

  }

  public getTokenType(status: AmendmentType): Type {
    let type = Type.NEUTRAL;
    switch (status) {
      case AmendmentType.INCLUDED:
        type = Type.NOTICE;
        break;
      case AmendmentType.EXCLUDED:
        type = Type.DANGER;
        break;
      case AmendmentType.CHANGED:
        type = Type.WARNING;
        break;
      case AmendmentType.ORIGINAL:
        type = Type.NEUTRAL;
        break;
    }
    return type;
  }

}

export {
  NegotiationContractualAmendmentController,
};
